import {
  LayoutDashboard, Percent, Users, Stethoscope, Brain, Megaphone, BadgeCheck, ShieldCheck,
  ArrowRight, Wallet,
} from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal, Img } from "./Reveal";
import { FRANCHISE_BENEFITS, FRANCHISE_STEPS, IMG } from "@/data/content";
import { FranchiseForm } from "./Forms";
import { scrollToId } from "@/lib/scroll";

const ICONS = { LayoutDashboard, Percent, Users, Stethoscope, Brain, Megaphone, BadgeCheck, ShieldCheck };

export function Franchise() {
  return (
    <section id="franchise" data-testid="franchise-section" className="py-24 lg:py-32 bg-ivory relative overflow-hidden">
      <div className="pointer-events-none absolute top-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-teal-brand/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 items-end">
          <SectionHeading
            chapter="11"
            overline="Franchise Opportunity"
            title="Build Your Own Dyntra Clinic"
            sub="Join a growing healthcare franchise ecosystem. Dyntra Clinics operates under a franchise model designed for entrepreneurs and healthcare professionals interested in establishing a modern dental and skin-care clinic."
          />
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-3xl shadow-lift">
              <Img src={IMG.franchiseMeet} alt="Franchise partnership discussion" className="h-56 w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
          </Reveal>
        </div>

        <Reveal>
          <div className="mt-10 rounded-3xl bg-pine-deep text-ivory p-8 md:p-10 relative overflow-hidden grain flex flex-col md:flex-row md:items-center gap-6 shadow-grand">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gold/15 text-gold">
              <Wallet className="h-7 w-7" strokeWidth={1.6} />
            </span>
            <div className="flex-1">
              <h3 className="font-display text-2xl md:text-3xl font-semibold">We Pay for Your Clinic.</h3>
              <p className="mt-2 text-sm text-ivory/70 max-w-xl">
                Dyntra invests in your success so you can focus on what matters most — your patients.
              </p>
            </div>
            <button
              data-testid="franchise-quote-cta"
              onClick={() => scrollToId("franchise-form")}
              className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-bold text-pine-deep transition-all duration-300 hover:bg-gold-soft hover:-translate-y-0.5 shrink-0"
            >
              Request a Quote <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {FRANCHISE_BENEFITS.map((b, i) => {
            const Icon = ICONS[b.icon] || BadgeCheck;
            return (
              <Reveal key={b.title} delay={(i % 4) * 0.05}>
                <div className="group h-full rounded-2xl border border-pine/10 bg-white p-5 transition-all duration-500 hover:-translate-y-1 hover:shadow-lift hover:border-gold/40">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-cream text-pine transition-colors duration-500 group-hover:bg-pine group-hover:text-gold">
                      <Icon className="h-5 w-5" strokeWidth={1.8} />
                    </span>
                    <span className="rounded-full bg-teal-soft px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-teal-brand">{b.tag}</span>
                  </div>
                  <p className="mt-3 text-sm font-bold text-pine-deep leading-snug">{b.title}</p>
                  <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-24">
          <Reveal>
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-pine-deep text-center">Your Franchise Journey</h3>
          </Reveal>
          <div className="mt-12 relative">
            <div className="hidden lg:block absolute top-7 left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
            <div className="grid lg:grid-cols-6 gap-8 lg:gap-4">
              {FRANCHISE_STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.06}>
                  <div data-testid={`franchise-step-${s.n}`} className="relative flex lg:flex-col items-start lg:items-center gap-4 lg:text-center">
                    <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-pine-deep text-gold font-display text-lg font-semibold shadow-lift ring-4 ring-ivory">
                      {s.n}
                    </span>
                    <div>
                      <h4 className="font-display text-base font-semibold text-pine-deep">{s.title}</h4>
                      <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 text-center">
              <button
                data-testid="franchise-journey-cta"
                onClick={() => scrollToId("franchise-form")}
                className="group inline-flex items-center gap-2 rounded-full bg-pine px-8 py-4 text-sm font-bold text-ivory shadow-grand transition-all duration-300 hover:bg-pine-deep hover:-translate-y-0.5"
              >
                Start Your Franchise Journey <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="mt-4 text-xs text-slate-400 max-w-md mx-auto">
                Franchise outcomes depend on location, investment and operations. Dyntra does not promise guaranteed revenue, profitability or patient numbers.
              </p>
            </div>
          </Reveal>
        </div>

        <div id="franchise-form" className="mt-20 grid lg:grid-cols-5 gap-8 items-stretch">
          <Reveal className="lg:col-span-2">
            <div className="relative h-full overflow-hidden rounded-3xl shadow-grand min-h-[280px]">
              <Img src={IMG.franchisePlan} alt="Planning a Dyntra Clinic launch" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-pine-deep/85 via-pine-deep/30 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-gold">Partner With Us</p>
                <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-ivory leading-snug">
                  Interested in Opening a Dyntra Clinic?
                </h3>
                <p className="mt-2 text-sm text-ivory/70">
                  Tell us about yourself — our franchise team will take it from there.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-3">
            <div className="h-full rounded-3xl bg-white border border-pine/10 shadow-lift p-7 md:p-9">
              <FranchiseForm />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
